import { Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { antIcon } from 'components/pages/Login';
import { getCustomerConfig } from 'config/currentCustomerConfig';
import { pricingSimulatorUrls, tradePromoOptimizationUrls } from 'config/urls';
import { ADMIN_ROUTE_QUERY_KEYS, ADMIN_ROUTE_STALE_TIME_MS } from 'constants/adminRoutes';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { fetchAdminRoutes } from 'hooks/adminRoutes/adminRoutes.api';
import react, { useState, createContext, FC, useContext, useEffect, useMemo } from 'react';
import { ErrorMessages, PricingSimulatorNotificationMessages } from 'utils/Enums';
import { downloadCsv, generateErrorMsg, getConfig, sendNotification } from 'utils/helperMethods';
import { mergeAdminRoutes } from 'utils/mergeAdminRoutes';

// function loadConfigBasedOnTenant(): Record<string, unknown> {
//     return getCustomerConfig();
// }

const UserContext = createContext<{
    appConfig: any;
    setAppConfig: React.Dispatch<React.SetStateAction<any>>;
    exportSimulatorOptions: {
        exportSimulatorScenario: Function;
        exportScenarioLoading: boolean;
    };
    exportPromoLibraryOptions: {
        exportToolsData: Function;
        exportPromoLibraryLoading: boolean;
    };
}>({
    appConfig: {},
    setAppConfig: () => {},
    exportSimulatorOptions: {
        exportScenarioLoading: false,
        exportSimulatorScenario: () => {},
    },
    exportPromoLibraryOptions: {
        exportPromoLibraryLoading: false,
        exportToolsData: () => {},
    },
});
interface props {
    children: React.ReactNode;
}

type Config = Record<string, any[]>;

const ConfigContext: FC<props> = ({ children }) => {
    // The tenant record on its own — the bundled module at boot, then whatever get-ui-config
    // returns at login. `appConfig` below is this plus the admin-managed routes.
    const [baseConfig, setBaseConfig] = useState<any>(null);
    const { mutate: exportScenario, loading: exportScenarioLoading } = useCommonMutationHandler({
        url: pricingSimulatorUrls.exportScenarioUrl,
        method: 'post',
    });

    const { mutate: exportPromoLibraryFn, loading: exportPromoLibraryLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.getPromoLibraryTableDataExportUrl,
        method: 'post',
    });

    useEffect(() => {
        getCustomerConfig().then(setBaseConfig);
    }, []);

    /**
     * Admin-managed embedded routes, fetched here rather than in `Login` because Login mounts
     * only on `/` and `/callback` — a hard refresh on a deep link never runs it, and the routes
     * would be missing exactly when the deep link needs them.
     *
     * Fails soft on purpose: an error leaves `data` undefined, the merge is a no-op and the app
     * renders its native screens. Nothing here may block the shell or reach for the spinner.
     */
    const isRouteManagementEnabled = getConfig('commonConfig', baseConfig)?.enableRouteManagement === true;
    const { data: adminRoutes } = useQuery({
        queryKey: ADMIN_ROUTE_QUERY_KEYS.list,
        queryFn: fetchAdminRoutes,
        // This provider also wraps the login screens, where there is no token to send yet.
        enabled: isRouteManagementEnabled && Boolean(localStorage.getItem('isAuthenticated')),
        staleTime: ADMIN_ROUTE_STALE_TIME_MS,
        retry: 1,
    });

    // Derived rather than stored: `Login` replaces the base config wholesale after get-ui-config,
    // and a merged copy held in its own state would be silently thrown away by that write.
    const appConfig = useMemo(() => mergeAdminRoutes(baseConfig, adminRoutes), [baseConfig, adminRoutes]);

    if (!baseConfig) {
        return <Spin indicator={antIcon} style={{ color: 'white' }} />; // or loading spinner
    }

    //adding function here so that it persists throughout the app
    function exportSimulatorScenario(payload: any, onSubmitClick: Function) {
        exportScenario(
            { ...payload },
            {
                onSuccess: (data) => {
                    const fileName = `${payload.project_name}_${payload.scenario_name}.csv`;
                    // Create a Blob from the CSV data
                    const blob = new Blob([data?.data], { type: 'text/csv' });

                    // Create a download link
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName); // Set file name
                    document.body.appendChild(link);
                    link.click();

                    // Clean up and remove the link
                    link.parentNode?.removeChild(link);

                    //setting task id in local storage, so that on refresh polling would still work
                    //  localStorage.setItem('export_task_id', data?.data?.task_id || ''); //DO IT LATER
                    // onEditSuccess();
                    sendNotification(
                        'success',
                        `${payload.scenario_name} Export is ready! The download has started automatically`,
                        8,
                        500,
                    );
                    onSubmitClick(data?.data?.task_id || '', false);
                },
                onError: (error: any) => {
                    const errorMsg = generateErrorMsg(
                        String(error?.response?.data?.detail || ''),
                        PricingSimulatorNotificationMessages.GENERALIZED_ERROR_MSG,
                    );
                    //show error message
                    sendNotification('error', errorMsg);
                    onSubmitClick('', false);
                },
            },
        );
    }
    //adding function here so that it persists throughout the app

    function exportToolsData(variables: any, fileName: string) {
        exportPromoLibraryFn(variables, {
            onSuccess: (data) => {
                downloadCsv([data?.data], fileName);
                sendNotification('success', `Export is ready! The download has started automatically`, 5, 500);
            },
            onError: (error: any) => {
                const errorMsg = generateErrorMsg(
                    String(error?.response?.data?.detail || ''),
                    ErrorMessages.GENERALIZED_ERROR_MSG,
                );
                //show error message
                sendNotification('error', errorMsg);
            },
        });
    }

    const exportSimulatorOptions = {
        exportScenarioLoading,
        exportSimulatorScenario,
    };

    const exportPromoLibraryOptions = {
        exportToolsData,
        exportPromoLibraryLoading,
    };
    return (
        <UserContext.Provider
            value={{ appConfig, setAppConfig: setBaseConfig, exportSimulatorOptions, exportPromoLibraryOptions }}
        >
            {children}
        </UserContext.Provider>
    );
};

export { UserContext, ConfigContext };
